import {
  Button,
  ButtonGroup,
  Card,
  Checkbox, Icon,
  Menu,
  MenuItem,
  Popover,
  ResizeSensor,
  Switch,
  Tag,
  Tooltip
} from '@blueprintjs/core'
import produce from 'immer'
import { cloneDeep, uniq } from 'lodash'
import React, { useMemo, useState, useCallback } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { isDescendant, walk } from 'react-sortable-tree'
import { NodeById } from '../../helper/classes'
import { OverlayProps } from '../../helper/configs'
import { globalOnDemandVars } from '../../helper/context'
import { hasScrollbarIfHover } from '../../helper/CSSclasses'
import { getNodeById, truncateDefault } from '../../helper/functions'
import voca from 'voca'
import { getTodayNumber, isDueDate } from '../../logic/functions'
import { getMemorizingStatus } from '../../logic/notebook'
import { rowToNode } from '../../logic/spreadsheet'
import { fragmentToNode } from '../../logic/uploadType2'
import { getNodeKey } from '../list/window1/functions'
import { PieChart } from 'react-minimal-pie-chart'
import step1Image from '../../../assets/images/step1.png'
import step2Image from '../../../assets/images/step2.png'
import step3Image from '../../../assets/images/step3.png'
import step4Image from '../../../assets/images/step4.png'
import step5Image from '../../../assets/images/step5.png'
import { Trans, useTranslation } from 'react-i18next'

export function updateStatistics (node, kind, result) {
  result.kind[kind] += 1
  result.questions += 1

  if (node.editableValues.failStatus && node.editableValues.failStatus !== 'solved') {
    result.predictedRatings.failed += 1
    result.growth.toReview += 1

    switch (node.editableValues.failStatus) {
      case 'reStudy':
        result.status.toLearnAgain += 1
        break
      case 'reTest':
        result.status.toTestAgain += 1
        break
    }
  } else {
    const predictedRating = getMemorizingStatus(node)
    if (predictedRating === 'firstTime') {
      result.predictedRatings.new += 1
      result.status.new += 1
      result.growth.new += 1
    } else {
      result.predictedRatings[predictedRating] += 1
      if (isDueDate(node)) {
        result.status.toReview += 1
        result.growth.toReview += 1
      } else {
        result.status.memorized += 1
        const dueDateMinusToday = (node.editableValues.update + node.editableValues.interval) - getTodayNumber(true)
        if (dueDateMinusToday <= 10) {
          result.growth.tenDays += 1
        } else if (dueDateMinusToday >= 100) {
          result.growth.hundredDays += 1
        } else {
          result.growth.tenHundredDays += 1
        }
      }
    }
  }
}

const regexForMediaBlock = /!\[.*?]\(.*?\)/g
const regexForMediaId = /!\[.*?]\((.*?)\)/
export function getStatistics (nodeIds, includeSkip) {
  const mediaIdSet = new Set()

  const result = {
    notes: 0,
    questions: 0,
    status: {
      memorized: 0,
      toReview: 0,
      new: 0,
      toLearnAgain: 0,
      toTestAgain: 0
    },
    predictedRatings: {
      easy: 0,
      normal: 0,
      hard: 0,
      fail: 0,
      new: 0,
      failed: 0
    },
    kind: {
      node: 0,
      row: 0,
      fragment: 0
    },
    growth: {
      new: 0,
      toReview: 0,
      tenDays: 0,
      tenHundredDays: 0,
      hundredDays: 0
    }
  }

  const originalGTD = globalOnDemandVars.globalTreeData()
  let treeData = originalGTD
  if (nodeIds) {
    if (!nodeIds.some((oneId) => (oneId === 'rootNode'))) {
      treeData = uniq(nodeIds).map((oneId) => (getNodeById(oneId, originalGTD)))
      treeData = treeData.filter((maybeNode) => (Boolean(maybeNode)))
      treeData = treeData.filter((oneNode) => (!(treeData.some((maybeAncestor) => (isDescendant(maybeAncestor, oneNode))))))
      treeData = treeData.map((originalNode) => ({
        ...originalNode,
        editableValues: {
          ...(originalNode.editableValues),
          parentId: 'rootNode'
        }
      }))
    }
  }
  // if (!includeSkip) {
  //   // includeSkip 이 아닐 때에만 treeData를 건드리는 거지?!
  //   treeData = cloneDeep(treeData)
  // }

  if (!includeSkip) {
    const skipIds = globalOnDemandVars.skipIds()
    if (skipIds.findIndex((skipId) => (skipId === 'rootNode')) !== -1) {
      treeData = []
    } else {
      treeData = treeData.filter((node) => (!skipIds.some((skipId) => {
        const skippedNode = (new NodeById(skipId)).getNode()
        return (skipId === node.id || (skippedNode ? isDescendant(skippedNode, node) : false))
      })))
    }
  }

  const walkCallback = ({
    node,
    parentNode,
    path,
    lowerSiblingCounts,
    treeIndex
  }) => {
    if (!includeSkip) {
      const skipIds = globalOnDemandVars.skipIds()
      if (skipIds.some((oneId) => (oneId === node.id || oneId === 'rootNode'))) {
        node.children = null
        node.isSpreadsheet = false
        node.isUploadType2 = false
        return true
      }
    }

    result.notes += 1

    if (node.subtitle) {
      updateStatistics(node, 'node', result)
    }

    if (node.isSpreadsheet) {
      const spreadsheetId = node.spreadsheetId
      const spreadsheet = globalOnDemandVars.spreadsheets()[spreadsheetId]
      let rows = spreadsheet.rows
      rows = rows.filter((row) => {
        const frontArray = spreadsheet.selectedItems1
        const backArray = spreadsheet.selectedItems2

        return frontArray.some((key) => !!row[key]) && backArray.some((key) => !!row[key])
      })
      const nodes = rows.map((row) => (rowToNode(row, spreadsheetId, 'spreadsheet')))

      for (const rowNode of nodes) {
        result.notes += 1
        updateStatistics(rowNode, 'row', result)
      }
    }

    if (node.isUploadType2) {
      const uploadType2Id = node.uploadType2Id
      const uploadType2 = globalOnDemandVars.uploadType2s()[uploadType2Id]
      const fragments = uploadType2.fragmentList
      const nodes = fragments.map((fragment) => (fragmentToNode(fragment, uploadType2Id, 'uploadType2')))

      for (const fragmentNode of nodes) {
        result.notes += 1
        updateStatistics(fragmentNode, 'fragment', result)
      }
    }

    const mediaStrings = (String(node.title) + String(node.subtitle)).match(regexForMediaBlock)
    if (mediaStrings) {
      mediaStrings
        .map((mediaString) => (regexForMediaId.exec(mediaString)[1]))
        .filter((mediaId) => (!mediaIdSet.has(mediaId)))
        .map((mediaId) => (globalOnDemandVars.uploadType2s()[mediaId]))
        .filter((maybeItem) => {
          return maybeItem && maybeItem.fragmentList && maybeItem.fragmentList.length
        })
        .forEach((mediaItem) => {
          mediaIdSet.add(mediaItem.id)

          const fragments = mediaItem.fragmentList
          const nodes = fragments.map((fragment) => (fragmentToNode(fragment, mediaItem.id, 'uploadType2')))

          for (const fragmentNode of nodes) {
            result.notes += 1
            updateStatistics(fragmentNode, 'fragment', result)
          }
        })
    }
  }

  walk({
    treeData: treeData,
    ignoreCollapsed: false,
    getNodeKey: getNodeKey,
    callback: walkCallback
  })

  return result
}

export function StatusChart ({ statistics, height = 200 }) {
  const { t } = useTranslation()

  const count = statistics.kind.node + statistics.kind.row + statistics.kind.fragment

  return (
    <div css={css`flex-grow: 1; display: flex; flex-direction: column; flex-basis: 450px; flex-grow: 1;`} style={{ height: height + 'px' }}>

      <div css={css`display: flex; height: 100%; position: relative; margin-top: 1.5em; flex-grow: 1;`}>

        <div css={css`position: relative; height: 100%; font-size: var(--s-1); width: 2.25em; color: var(--gray10);
& > div { position: absolute; }
`}>
          <div css={css`bottom: calc(30% - var(--s-2));`}>30%</div>
          <div css={css`bottom: calc(60% - var(--s-2));`}>60%</div>
          <div css={css`bottom: calc(90% - var(--s-2));`}>90%</div>
        </div>

        <div css={css`position: relative; height: 100%; flex-grow: 1; border-bottom: 2px solid var(--gray4); margin-right: 1em;`}>
          <div css={css`
& > div {
  position: absolute; bottom: 0; width: 2.25em; background-color: var(--primary3); text-align: center; color: var(--primary3); z-index: 2;
}
& > div > div {
  position: relative; bottom: 1.5em; text-align: center; font-weight: bold; font-size: var(--s-1);
}
          `}>
            <div css={css`right: 80%;`} style={{ height: ((statistics.growth.new / count) * 100) + '%' }}>
              <div>{statistics.growth.new}</div>
            </div>
            <div css={css`right: 60%;`} style={{ height: ((statistics.growth.toReview / count) * 100) + '%' }}>
              <div>{statistics.growth.toReview}</div>
            </div>
            <div css={css`right: 40%;`} style={{ height: ((statistics.growth.tenDays / count) * 100) + '%' }}>
              <div>{statistics.growth.tenDays}</div>
            </div>
            <div css={css`right: 20%;`} style={{ height: ((statistics.growth.tenHundredDays / count) * 100) + '%' }}>
              <div>{statistics.growth.tenHundredDays}</div>
            </div>
            <div css={css`right: 0;`} style={{ height: ((statistics.growth.hundredDays / count) * 100) + '%' }}>
              <div>{statistics.growth.hundredDays}</div>
            </div>
          </div>

          <div css={css`
& > div {
  width: 100%; height: 1px; background-color: var(--gray4); position: absolute;
}
          `}>
            <div css={css`bottom: 30%;`} />
            <div css={css`bottom: 60%;`} />
            <div css={css`bottom: 90%;`} />
          </div>
        </div>
      </div>

      <div css={css`position: relative; margin-top: 1em; height: 1.5em; font-size: 0.9em; font-weight: bold;
& > div {
  position: absolute; bottom: 0;
}
`}>
        <div css={css`right: calc(80% - 1em);`}>{t('New')}</div>
        <div css={css`right: calc(60% - 2.25em);`}>{t('To Review')}</div>
        <div css={css`right: calc(40% - 1em);`}>{t('~ 10 days')}</div>
        <div css={css`right: calc(20% - 1.5em);`}>{t('10 ~ 100 days')}</div>
        <div css={css`right: calc(0%);`}>{t('100 ~ days')}</div>
      </div>
    </div>
  )
}

function NoteChart ({ chartId, openDialog, remove, sharingAddonMode, inDialog }) {
  const { t } = useTranslation()

  const nodeIds = useMemo(() => (sharingAddonMode ? [globalOnDemandVars.sharedNodeId()] : globalOnDemandVars.nodeIdsOfCharts()[chartId]), [chartId, sharingAddonMode])
  const nodeTitles = useMemo(() => (nodeIds.map((id) => {
    if (id === 'rootNode') {
      return globalOnDemandVars.topRootName()
    }
    const nodeById = getNodeById(id, globalOnDemandVars.globalTreeData())
    return nodeById ? nodeById.title : undefined
  })), [nodeIds])

  const [booleans, setBooleans] = useState(sharingAddonMode ? {
    status: true,
    predictedRatings: true,
    kind: true,
    includeSkippedNotes: false
  } : globalOnDemandVars.booleansOfCharts()[chartId])

  const statistics = useMemo(() => (getStatistics(nodeIds, booleans.includeSkippedNotes)), [booleans.includeSkippedNotes, nodeIds])

  const labelF = useCallback(
    ({ dataEntry }) => (dataEntry.value ? (`${dataEntry.title}: ${dataEntry.value} (${Math.round(dataEntry.percentage)}%)`) : ''),
    []
  )
  const labelProps = useMemo(() => ({
    labelStyle: {
      fontSize: '5px',
      fill: 'var(--reverseColor)',
      fontWeight: 'bold'
      // textShadow: '-1px 0 var(--gray5), 0 1px var(--gray5), 1px 0 var(--gray5), 0 -1px var(--gray5)'
    }
  }), [])

  const [size, setSize] = useState({ width: 0, height: 0 })

  const showButton = useMemo(() => (
    <Popover {...OverlayProps} position={'bottom'} minimal content={(
      <Menu>
        {['Status', 'Predicted Ratings', 'Kind'].map((label) => {
          const camelCasedLabel = voca.camelCase(label)
          return (
            <MenuItem key={label} text={t(label)} icon={booleans[camelCasedLabel] ? 'tick' : 'none'} onClick={() => {
              const newBooleans = produce(booleans, draft => {
                draft[camelCasedLabel] = !booleans[camelCasedLabel]
              })
              setBooleans(newBooleans)
              if (!sharingAddonMode) {
                globalOnDemandVars.set_booleansOfCharts(produce(globalOnDemandVars.booleansOfCharts(), draft => {
                  draft[chartId] = newBooleans
                }))
              }
            }} />
          )
        })}
      </Menu>
    )}>
      <Button text={t('Show')} />
    </Popover>
  ), [booleans, chartId, sharingAddonMode, t])

  const charts = useMemo(() => {
    const count = statistics.kind.node + statistics.kind.row + statistics.kind.fragment

    return (
      <div css={css`display: flex; flex-direction: column; width: 100%;`}>
        {(booleans.status ? (<div css={css`min-height: 320px; margin-bottom: 3.375em; padding: 2.25em;`} key={'status'}>
          <div css={css`display: flex; flex-wrap: wrap; flex-direction: column; margin: auto; max-width: 600px;`}>
            <div css={css`position: relative;
//left: 4%;
`}>{<StatusChart statistics={statistics} />}</div>
            <div css={css`display: flex; gap: 1em; font-size: 1.5em; font-weight: bold; justify-content: center; align-content: center; align-items: center; text-align: center; border: 1px solid var(--gray4); margin: 1rem; margin-bottom: 0; padding: 1rem;`}>
              <div>{t('totalNotes', { count: statistics.notes })}</div>
              <div>{t('totalQuestions', { count: count })}</div>
            </div>
          </div>
        </div>) : null)}

        <div css={css`display: flex; flex-direction: column; gap: var(--s5); width: 100%;`}>
          {
            [(booleans.predictedRatings ? (<div className={'chartContainer'} key={'pR'}>
              {/* <div className='title'><Tag css={css`background-color: #ff5400;`}>Predicted Ratings</Tag></div> */}
              <div className={'chartContainer2'}>
                <div className={'chartLabel'} css={css`top: calc(50% - 1em); left: calc(50% - 2.25em);`}>
                  <Trans i18nKey={'predictedRatings'}>Predicted<br />Ratings</Trans>
                </div>
                <PieChart lineWidth={15} labelPosition={112} data={[
                  { title: t('Easy'), value: statistics.predictedRatings.easy, color: '#D84E39' },
                  { title: t('Normal'), value: statistics.predictedRatings.normal, color: '#FC8862' },
                  { title: t('Hard'), value: statistics.predictedRatings.hard, color: '#FFC1AD' },
                  // { title: 'Fail', value: statistics.predictedRatings.fail, color: '#ade261' },
                  { title: t('New'), value: statistics.predictedRatings.new, color: '#FFB145' },
                  {
                    title: t('Fail'),
                    value: (statistics.predictedRatings.failed + statistics.predictedRatings.fail),
                    color: '#FFFF88'
                  }
                ]} label={labelF} {...labelProps} />
              </div>

              <Card className={'labelCard'}>
                <div>
                  <div className={'colorPart'} css={css`background-color: #D84E39;`} />
                  <div className={'labelPart'}>
                    {t('Easy')}
                  </div>
                  <div>
                    {`${statistics.predictedRatings.easy}(${Math.round((statistics.predictedRatings.easy / statistics.questions) * 100)}%)`}
                  </div>
                </div>

                <div>
                  <div className={'colorPart'} css={css`background-color: #FC8862;`} />
                  <div className={'labelPart'}>
                    {t('Normal')}
                  </div>
                  <div>
                    {`${statistics.predictedRatings.normal}(${Math.round((statistics.predictedRatings.normal / statistics.questions) * 100)}%)`}
                  </div>
                </div>

                <div>
                  <div className={'colorPart'} css={css`background-color: #FFC1AD;`} />
                  <div className={'labelPart'}>
                    {t('Hard')}
                  </div>
                  <div>
                    {`${statistics.predictedRatings.hard}(${Math.round((statistics.predictedRatings.hard / statistics.questions) * 100)}%)`}
                  </div>
                </div>

                <div>
                  <div className={'colorPart'} css={css`background-color: #FFB145;`} />
                  <div className={'labelPart'}>
                    {t('New')}
                  </div>
                  <div>
                    {`${statistics.predictedRatings.new}(${Math.round((statistics.predictedRatings.new / statistics.questions) * 100)}%)`}
                  </div>
                </div>

                <div>
                  <div className={'colorPart'} css={css`background-color: #FFFF88;`} />
                  <div className={'labelPart'}>
                    {t('Fail')}
                  </div>
                  <div>
                    {`${(statistics.predictedRatings.failed + statistics.predictedRatings.fail)}(${Math.round(((statistics.predictedRatings.failed + statistics.predictedRatings.fail) / statistics.questions) * 100)}%)`}
                  </div>
                </div>
              </Card>
            </div>) : null),
            (booleans.kind ? (<div className={'chartContainer'} key={'kind'}>
              <div className={'chartContainer2'}>
                <div className={'chartLabel'} css={css`top: calc(50% - 1em); left: calc(50% - 1em);`}>
                  {t('Kind')}
                </div>
                <PieChart lineWidth={15} labelPosition={112} data={[
                  { title: t('Node'), value: statistics.kind.node, color: '#48AFF0' },
                  { title: t('Row'), value: statistics.kind.row, color: '#137CBD' },
                  { title: t('Fragment'), value: statistics.kind.fragment, color: '#0E5A8A' }
                ]} label={labelF} {...labelProps} />
              </div>

              <Card className={'labelCard'}>
                <div>
                  <div className={'colorPart'} css={css`background-color: #48AFF0;`} />
                  <div className={'labelPart'}>
                    {t('Node')}
                  </div>
                  <div>
                    {`${statistics.kind.node}(${Math.round((statistics.kind.node / statistics.questions) * 100)}%)`}
                  </div>
                </div>

                <div>
                  <div className={'colorPart'} css={css`background-color: #137CBD;`} />
                  <div className={'labelPart'}>
                    {t('Row')}
                  </div>
                  <div>
                    {`${statistics.kind.row}(${Math.round((statistics.kind.row / statistics.questions) * 100)}%)`}
                  </div>
                </div>

                <div>
                  <div className={'colorPart'} css={css`background-color: #0E5A8A;`} />
                  <div className={'labelPart'}>
                    {t('Fragment')}
                  </div>
                  <div>
                    {`${statistics.kind.fragment}(${Math.round((statistics.kind.fragment / statistics.questions) * 100)}%)`}
                  </div>
                </div>
              </Card>
            </div>) : null), (
              (booleans.predictedRatings || booleans.kind) ? (<div />) : null
            )]
          }
        </div>
      </div>
    )
  }, [booleans.kind, booleans.predictedRatings, booleans.status, labelF, labelProps, statistics, t])

  return useMemo(() => (
    <ResizeSensor onResize={(entries) => {
      const width = entries[0].contentRect.width
      const height = entries[0].contentRect.height
      setSize({ width, height })
    }}>
      <div css={css`position: relative; width: 100%; height: 100%; background-color: var(--baseColor); border-radius: var(--s-1); ${inDialog ? '' : 'border: var(--gray2) 1px solid;'}
      display: flex;
      flex-direction: column;`}>
        <div css={css`display: flex;`}>
          <div css={css`overflow: auto;
          flex-grow: 1;
          max-height: 8em;
          display: flex;
          flex-wrap: wrap;

          .bp3-tag span {
            padding: 0.67rem;
          } `}>{
              nodeTitles.map((title, index, array) => (
                <Tag minimal css={css`font-size: 1.5em; font-weight: bold; margin: 6px; background-color: transparent !important;`} title={title} key={index}>{title ? truncateDefault(title, (array.length >= 2)) : (
                  title !== undefined ? (<span css={css`opacity: 0.5;`}>{t('Blank')}</span>) : (
                    <span css={css`opacity: 0.5;`}>{t('Deleted')}</span>
                  ))}</Tag>
              ))
            }</div>

          <div css={css`display: flex; flex-direction: column; align-items: flex-end;`}>
            <div css={css`display: flex; flex-wrap: wrap; margin: var(--s-2);`}>
              <div css={css`padding: 4px 0 0 1em;`}>
                <Switch label={t('Include Skipped Notes')} checked={booleans.includeSkippedNotes} inline onChange={(e) => {
                  const newBooleans = produce(booleans, draft => {
                    draft.includeSkippedNotes = !booleans.includeSkippedNotes
                  })
                  setBooleans(newBooleans)
                  if (!sharingAddonMode) {
                    globalOnDemandVars.set_booleansOfCharts(produce(globalOnDemandVars.booleansOfCharts(), draft => {
                      draft[chartId] = newBooleans
                    }))
                  }
                }} />
              </div>

              {sharingAddonMode || inDialog ? null : <ButtonGroup minimal>
                <Tooltip content={t('Open in a Big Window')} position={'top'} {...OverlayProps}>
                  <Button icon={'zoom-in'} onClick={openDialog} />
                </Tooltip>
                <Tooltip content={t('Remove from the Board')} position={'top'} {...OverlayProps}>
                  <Button icon={'cross'} onClick={remove} />
                </Tooltip>
              </ButtonGroup>}
            </div>
          </div>
        </div>

        <div css={css`padding: 0;
        position: relative;
        ${sharingAddonMode ? '' : 'flex: 1 1 0;'}
        overflow: ${sharingAddonMode ? 'visible' : 'auto'};
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;`}>

          <div css={css`position: absolute; top: 0; right: var(--s-1);`}>
            {showButton}
          </div>

          {(size.width < 200 || size.height < 200) ? (
            <div css={css`display: flex; justify-content: center; align-items: center;`}>
              <Icon css={css`&:hover { cursor: pointer; }`} icon={'zoom-in'} iconSize={36} color={'var(--gray4)'} onClick={openDialog} />
            </div>) : (<div className={hasScrollbarIfHover} css={css`padding-top: 4px;
          flex-grow: 1;
          overflow: auto;
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-evenly;

          & .chartContainer {
            position: relative;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: var(--s3);
            align-items: center;
            max-width: 1100px;
            width: 100%;
            margin: auto;
          }
          
          & .chartContainer2 {
            position: relative;
            height: var(--s7); width: var(--s8);
          }
          
          & .labelCard {
            height: fit-content !important; font-weight: bold; padding: 2.25em !important; flex-basis: 400px; flex-shrink: 1; display: flex; flex-direction: column; gap: var(--s-1);
            & > div {
              display: flex; gap: 1em;
            }
            & .colorPart {
              width: 1.5em; height: 1.5em; border-radius: 1em;
            }
            & .labelPart {
              flex-grow: 1;
            }
          }
          
          & .chartLabel {
            position: absolute; font-size: 1.5em; font-weight: bold; text-align: center;
          }

          & .title {
            text-align: center;
          }
          & .bp3-tag {
            font-size: 1em;
            font-weight: bold;
            padding: var(--s-2) var(--s-2);
          }

          & svg {
            overflow: visible;
          }

          & .bp3-card {
            padding-top: 0;
            padding-bottom: 30px;
            height: 100%;
          }
        `}>
              {charts}
            </div>
          )}
        </div>
      </div>
    </ResizeSensor>
  ), [inDialog, nodeTitles, t, booleans, sharingAddonMode, openDialog, remove, showButton, size.width, size.height, charts, chartId])
}

export default NoteChart
